import { useMemo, useState } from 'react'

import { Stack, Text } from '@mantine/core'
import dayjs from 'dayjs'

import { usdFormatter } from '@repo/common/helpers/formatters'
import type { FundAssetNavsResponse } from '@repo/common/queries/admin/fund'

import {
  column,
  mapColumn,
  TowerDataTable,
  type TowerDataTableColumn,
} from '@/components/ui/TowerDataTable'

interface AssetNavRowData {
  id: string
  asset_name: string
  category: string
  fission_ticker: string | null
  shares_held: number
  shares_total_cost: number
  shares_value_held: number
  effective_at: string
}

interface AssetNavTableProps {
  data: FundAssetNavsResponse | null | undefined
  isLoading?: boolean
  effective_at?: string
}

export function AssetNavTable({
  data,
  isLoading = false,
  effective_at,
}: AssetNavTableProps) {
  const [sortBy, setSortBy] = useState<string | null>('asset_name')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  const processedData = useMemo(() => {
    if (!data?.assets.nodes) return []
    const rows: AssetNavRowData[] = []

    data.assets.nodes.forEach((assetNode) => {
      const asset = assetNode.asset

      const filteredNavs = effective_at
        ? assetNode.navs.nodes.filter(
            (nav) =>
              dayjs(nav.effective_at).valueOf() <=
              dayjs(effective_at).valueOf(),
          )
        : assetNode.navs.nodes

      if (filteredNavs.length > 0) {
        // generally there should only be one, but just in case, pick the most recent one
        const mostRecentNav = filteredNavs.reduce((latest, current) =>
          dayjs(current.effective_at).valueOf() >
          dayjs(latest.effective_at).valueOf()
            ? current
            : latest,
        )

        rows.push(
          Object.assign({}, mostRecentNav, {
            id: `${asset.id}-${mostRecentNav.effective_at}`,
            asset_name: asset.name,
            category: asset.category,
            fission_ticker: asset.fission_ticker,
            effective_at: mostRecentNav.effective_at,
          }),
        )
      }
    })

    // return rows.sort((a, b) => a.asset_name.localeCompare(b.asset_name));

    if (sortBy) {
      return rows.sort((a, b) => {
        const aValue = a[sortBy as keyof AssetNavRowData]
        const bValue = b[sortBy as keyof AssetNavRowData]
        if (aValue === null || bValue === null) return aValue === null ? 1 : -1 // push nulls to the end
        return (
          (aValue < bValue ? -1 : aValue > bValue ? 1 : 0) *
          (sortDirection === 'asc' ? 1 : -1)
        )
      })
    }

    return rows
  }, [data, effective_at, sortBy, sortDirection])

  const columns: TowerDataTableColumn<AssetNavRowData>[] = [
    column({
      accessor: 'asset_name',
      title: 'Asset Name',
      sortable: true,
      render: (name: string, row) => (
        <Stack gap={0}>
          <Text size="sm" fw={500}>
            {name}
          </Text>
          <Text size="xs" c="dimmed">
            {
              { external_fund: 'External Fund', company: 'Company ' }[
                row.category
              ]
            }
          </Text>
        </Stack>
      ),
    }),
    column({
      accessor: 'shares_held',
      title: 'Shares Held',
      textAlign: 'right',
      sortable: true,
      render: (shares: number) => (
        <Text size="sm">
          {new Intl.NumberFormat('en-US', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 0,
          }).format(shares)}
        </Text>
      ),
    }),
    mapColumn({
      title: 'Price',
      textAlign: 'right',
      sortable: true,
      render(_, row) {
        const price = row.shares_held
          ? row.shares_value_held / row.shares_held
          : 0
        return <Text>{usdFormatter(price)}</Text>
      },
    }),
    mapColumn({
      title: 'Total Value',
      textAlign: 'right',
      sortable: true,
      render(_, row) {
        return (
          <Stack gap={0}>
            <Text size="sm">{usdFormatter(row.shares_value_held)}</Text>
            <Text size="xs" c="dimmed">
              Cost {usdFormatter(row.shares_total_cost)}
            </Text>
          </Stack>
        )
      },
    }),
  ]

  return (
    <TowerDataTable
      data={processedData}
      columns={columns}
      isLoading={isLoading}
      onSort={(_, accessor) => {
        if (!accessor || typeof accessor === 'function') return
        const columnKey = accessor as string
        if (sortBy === columnKey) {
          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
        } else {
          setSortBy(columnKey)
          setSortDirection('asc')
        }
      }}
      emptyMessage={
        effective_at
          ? 'No asset nav data available for the selected date'
          : 'No asset nav data available'
      }
      withTableBorder
      withColumnBorders
      highlightOnHover
      striped
    />
  )
}
